import type { ReactNode } from 'react';

import { Pressable } from '../primitives/Pressable.js';
import type { PressableProps } from '../primitives/Pressable.js';
import { cx } from '../primitives/cx.js';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'gold' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variantClass: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-on-accent hover:bg-accent-hi',
  secondary: 'bg-raised text-ink border border-line hover:border-line-strong',
  ghost: 'bg-transparent text-muted hover:text-ink hover:bg-accent-soft',
  gold: 'bg-gold text-on-gold hover:brightness-110',
  danger: 'bg-danger-soft text-danger hover:brightness-110',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm rounded-md gap-1.5',
  md: 'h-11 px-4 text-base rounded-lg gap-2',
  lg: 'h-13 px-6 text-md rounded-xl gap-2',
};

export type ButtonProps = PressableProps & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconEnd?: IconName;
  fullWidth?: boolean;
  children?: ReactNode;
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconEnd,
  fullWidth = false,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <Pressable
      className={cx(
        'inline-flex items-center justify-center font-semibold',
        variantClass[variant],
        sizeClass[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} />}
      {children}
      {iconEnd && <Icon name={iconEnd} size={size === 'sm' ? 16 : 18} />}
    </Pressable>
  );
}
